import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Grid2 } from "@mui/material";
import { AppContext } from "context/AppContextProvider";
import DeviceDetailsByDiscriminator from "pages/DevicesPages/_components/DeviceDetailsByDiscriminator";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useCreateIvsDeviceAsync,
  useUpdateIvsDeviceAsync,
} from "services/Devices/IvsService/hooks/useMutation";
import {
  useCreateLprDeviceAsync,
  useUpdateLprDeviceAsync,
} from "services/Devices/LprService/hooks/useMutation";
import {
  useCreateRecognitionCameraAsync,
  useUpdateRecognitionCameraAsync,
} from "services/Devices/Recognition/Cameras/Intelbras/RecognitionCameraService/hooks/useMutation";
import {
  useCreateControlIdDeviceAsync,
  useUpdateControlIdAsync,
} from "services/Devices/Recognition/Controllers/ControlId/hooks/useMutation";
import {
  useCreateFacialControllerAsync,
  useUpdateFacialControllerAsync,
} from "services/Devices/Recognition/Controllers/hooks/useMutation";
import {
  useCreateTemperatureDetectorDeviceAsync,
  useUpdateTemperatureDetectorDeviceAsync,
} from "services/Devices/TemperatureDetectorService/hooks/useMutation";
import type { DeviceDiscriminator, IDeviceProps } from "services/Devices/types";
import type zod from "zod";
import {
  useCreateDvrDeviceAsync,
  useUpdateDvrDeviceAsync,
} from "../../services/Devices/DvrService/hooks/useMutation";
import { DeviceFormSchema } from "../../services/Devices/schemas";
import { UC_OPTIONS } from "../../config/ucOptions";
import DropdownComponent from "../DropdownComponent";
import DropdownSelectComponent from "../DropdownSelectComponent";
import ModalComponent from "../ModalComponent";
import RadioSelectionComponent from "../RadioSelectionComponent";
import TextFieldComponent from "../TextFieldComponent";

type DeviceFormInput = zod.infer<typeof DeviceFormSchema>;

interface DeviceEditionComponentProps {
  isOpen: boolean;
  handleSaveForms: (val: any, reset: () => void) => void;
  brands: any[];
  availableEvents?: any[];
  deviceToEdit?: any;
  setDeviceToEdit?: Dispatch<SetStateAction<any>>;
  isLoading?: boolean;
  discriminator?: DeviceDiscriminator;
  isDvr?: boolean;
  isControllers?: boolean;
  isCam?: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const DeviceEditionComponent = ({
  isOpen,
  setIsOpen,
  handleSaveForms,
  availableEvents: _availableEvents,
  deviceToEdit,
  setDeviceToEdit,
  isLoading: _isLoading,
  brands,
  discriminator,
  isControllers,
  isCam,
  isDvr,
}: DeviceEditionComponentProps) => {
  const { t } = useTranslation();
  const [deviceTypeSelected, setDeviceTypeSelected] =
    useState<DeviceDiscriminator>();
  const loadedBrandRef = useRef<string | undefined>(undefined);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const { showSnackbar } = useContext(AppContext);
  const [availableEventsOptions, setAvailableEventsOptions] = useState<any[]>(
    [],
  );
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors: _errors },
  } = useForm<DeviceFormInput>({
    resolver: zodResolver(DeviceFormSchema),
    defaultValues: {
      id: undefined,
      availableEvents: [],
      connection: 0,
      brand: "INTELBRAS",
      password: "",
      channel: 0,
      username: "admin",
      httpPort: "80",
      name: "",
      model: "",
      tcpPort: "37777",
      rtspPort: "554",
      ip: "",
      serialNumber: "",
      numberOfChannels: 0,
      numberOfAlarmEntries: 0,
      uc: null,
    },
  });

  const {
    mutateAsync: mutateCreateIvsDevice,
    isLoading: isLoadingCreateIvsDevice,
  } = useCreateIvsDeviceAsync();
  const {
    mutateAsync: mutateUpdateIvsDevice,
    isLoading: isLoadingUpdateIvsDevice,
  } = useUpdateIvsDeviceAsync();

  const {
    mutateAsync: mutateCreateLprDevice,
    isLoading: isLoadingCreateLprDevice,
  } = useCreateLprDeviceAsync();
  const {
    mutateAsync: mutateUpdateLprDevice,
    isLoading: isLoadingUpdateLprDevice,
  } = useUpdateLprDeviceAsync();

  const {
    mutateAsync: mutateCreateRecognitionCamera,
    isLoading: isLoadingCreateRecognitionCamera,
  } = useCreateRecognitionCameraAsync();
  const {
    mutateAsync: mutateUpdateRecognitionCamera,
    isLoading: isLoadingUpdateRecognitionCamera,
  } = useUpdateRecognitionCameraAsync();

  const {
    mutateAsync: mutateCreateTemperatureDetectorDevice,
    isLoading: isLoadingCreateTemperatureDetectorDevice,
  } = useCreateTemperatureDetectorDeviceAsync();
  const {
    mutateAsync: mutateUpdateTemperatureDetectorDevice,
    isLoading: isLoadingUpdateTemperatureDetectorDevice,
  } = useUpdateTemperatureDetectorDeviceAsync();

  const {
    mutateAsync: mutateCreateControlIdDevice,
    isLoading: isLoadingCreateControlIdDevice,
  } = useCreateControlIdDeviceAsync();
  const {
    mutateAsync: mutateUpdateControlIdDevice,
    isLoading: isLoadingUpdateControlIdDevice,
  } = useUpdateControlIdAsync();

  const {
    mutateAsync: mutateCreateFacialController,
    isLoading: isLoadingCreateFacialController,
  } = useCreateFacialControllerAsync();
  const {
    mutateAsync: mutateUpdateFacialController,
    isLoading: isLoadingUpdateFacialController,
  } = useUpdateFacialControllerAsync();

  const { mutateAsync: mutateCreateDvr, isLoading: isLoadingCreateDvr } =
    useCreateDvrDeviceAsync();
  const { mutateAsync: mutateUpdateDvr, isLoading: isLoadingUpdateDvr } =
    useUpdateDvrDeviceAsync();

  const handleSaveForm = () => {
    setSubmitAttempted(true);
    handleSubmit((data) => {
      handleSave(data, reset);
    })();
  };

  const handleSave = async (data: any, reset: () => void) => {
    let result: unknown;
    if (data.id) {
      switch (deviceTypeSelected) {
        case "IvsCameraEntity":
          result = await mutateUpdateIvsDevice({ data, id: data.id });
          break;
        case "LprCameraEntity":
          result = await mutateUpdateLprDevice({ data, id: data.id });
          break;
        case "RecognitionCameraEntity":
          result = await mutateUpdateRecognitionCamera({ data, id: data.id });
          break;
        case "TemperatureDetectorEntity":
          result = await mutateUpdateTemperatureDetectorDevice({
            data,
            id: data.id,
          });
          break;
        case "ControlIdDeviceEntity":
          result = await mutateUpdateControlIdDevice({
            data,
            id: data.id,
          });
          break;
        case "FacialControllerEntity":
          result = await mutateUpdateFacialController({
            data,
            id: data.id,
          });
          break;
        case "DvrEntity":
          result = await mutateUpdateDvr({
            data,
            id: data.id,
          });
          break;
        default:
          return;
      }

      showSnackbar(t("deviceEditionModal.updateSuccess"), "success");
      handleSaveForms(data, reset);
      if (setDeviceToEdit) setDeviceToEdit({} as IDeviceProps);
      setDeviceTypeSelected(undefined);
      setIsOpen(false);
      return result;
    }

    switch (deviceTypeSelected) {
      case "IvsCameraEntity":
        result = await mutateCreateIvsDevice(data);
        break;
      case "LprCameraEntity":
        result = await mutateCreateLprDevice(data);
        break;
      case "RecognitionCameraEntity":
        result = await mutateCreateRecognitionCamera(data);
        break;
      case "TemperatureDetectorEntity":
        result = await mutateCreateTemperatureDetectorDevice(data);
        break;
      case "ControlIdDeviceEntity":
        result = await mutateCreateControlIdDevice(data);
        break;
      case "FacialControllerEntity":
        result = await mutateCreateFacialController(data);
        break;
      case "DvrEntity":
        result = await mutateCreateDvr(data);
        break;
      default:
        return;
    }

    showSnackbar(t("deviceEditionModal.createSuccess"), "success");
    handleSaveForms(data, reset);
    setDeviceTypeSelected(undefined);
    setIsOpen(false);
    return result;
  };

  const deviceTypeOptions = () => {
    const deviceTypes: {
      label: string;
      value: DeviceDiscriminator;
      type: "CONTROLLER" | "CAMERA" | "DVR";
      isVisible?: boolean;
    }[] = [
        {
          label: t("deviceEditionModal.deviceTypes.recognitionCamera"),
          value: "RecognitionCameraEntity",
          type: "CAMERA",
          isVisible: isCam,
        },
        {
          label: t("deviceEditionModal.deviceTypes.lpr"),
          value: "LprCameraEntity",
          type: "CAMERA",
          isVisible: isCam,
        },
        {
          label: t("deviceEditionModal.deviceTypes.ivs"),
          value: "IvsCameraEntity",
          type: "CAMERA",
          isVisible: isCam,
        },
        {
          label: t("deviceEditionModal.deviceTypes.temperature"),
          value: "TemperatureDetectorEntity",
          type: "CAMERA",
          isVisible: isCam,
        },
        {
          label: t("deviceEditionModal.deviceTypes.controlId"),
          value: "ControlIdDeviceEntity",
          type: "CONTROLLER",
          isVisible: isControllers,
        },
        {
          label: t("deviceEditionModal.deviceTypes.commonController"),
          value: "FacialControllerEntity",
          type: "CONTROLLER",
          isVisible: isControllers,
        },
        {
          label: t("deviceEditionModal.deviceTypes.dvr"),
          value: "DvrEntity",
          type: "DVR",
          isVisible: isDvr,
        },
      ];

    return deviceTypes.filter((item) => item.isVisible);
  };
  const connectionOptions = [
    { label: t("deviceEditionModal.connectionOptions.direct"), value: 0 },
    { label: t("deviceEditionModal.connectionOptions.autoRegister"), value: 1 },
  ];

  const onClose = () => {
    reset();
    setIsOpen(false);
    setDeviceTypeSelected(undefined);
    setSubmitAttempted(false);
    if (setDeviceToEdit) setDeviceToEdit({});
  };

  const watchAvailableEvents = watch("availableEvents");
  const watchConnection = watch("connection");
  const watchBrand = watch("brand");

  const setDefaultType = useCallback(() => {
    if (isDvr && !deviceToEdit?.id) {
      setDeviceTypeSelected("DvrEntity");
    } else if (isControllers && !deviceToEdit?.id) {
      setDeviceTypeSelected("FacialControllerEntity");
    }
  }, [isDvr, isControllers, deviceToEdit]);

  useEffect(() => {
    if (!watchAvailableEvents) setValue("availableEvents", []);
  }, [watchAvailableEvents, setValue]);

  useEffect(() => {
    if (deviceToEdit?.id) {
      setDeviceTypeSelected(discriminator);
      reset(deviceToEdit, { keepDefaultValues: true });
      loadedBrandRef.current = deviceToEdit.brand;
    } else {
      reset();
      setDefaultType();
      loadedBrandRef.current = "INTELBRAS";
    }
  }, [deviceToEdit, setDefaultType, discriminator, reset]);

  useEffect(() => {
    if (deviceTypeSelected) {
      setAvailableEventsOptions(
        DeviceDetailsByDiscriminator(deviceTypeSelected)?.availableEvents ?? [],
      );
    }
  }, [deviceTypeSelected]);

  useEffect(() => {
    if (deviceTypeSelected === "ControlIdDeviceEntity") {
      setValue("brand", "CONTROLID");
    } else if (watchBrand === "CONTROLID") {
      setValue("brand", "INTELBRAS");
    }
  }, [deviceTypeSelected, watchBrand, setValue]);

  useEffect(() => {
    if (loadedBrandRef.current === watchBrand) return;
    loadedBrandRef.current = watchBrand;

    if (watchBrand === "HIKVISION") {
      setValue("tcpPort", "8000");
    } else if (watchBrand === "INTELBRAS") {
      setValue("tcpPort", "37777");
    }
  }, [watchBrand, setValue]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t("deviceEditionModal.title")}
    >
      <Grid2
        container
        sx={{ mt: 2 }}
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
      >
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                label={`${t("deviceEditionModal.name")} *`}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={"name"}
            control={control}
          />
        </Grid2>
        <Grid2 size={6}>
          <DropdownComponent
            optionLabel={"label"}
            options={deviceTypeOptions()}
            optionValue="value"
            label={`${t("deviceEditionModal.type")} *`}
            errorMessage={
              submitAttempted && !deviceTypeSelected
                ? t("common.mandatoryField")
                : undefined
            }
            disabled={deviceToEdit?.id !== undefined || isDvr}
            onChange={setDeviceTypeSelected}
            value={deviceTypeSelected}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <DropdownComponent
                options={
                  deviceTypeSelected === "ControlIdDeviceEntity"
                    ? brands.filter((b) => b.value === "CONTROLID")
                    : brands.filter((b) => b.value !== "CONTROLID")
                }
                optionLabel={"label"}
                optionValue={"value"}
                value={value}
                onChange={onChange}
                label={`${t("deviceEditionModal.brand")} *`}
                disabled={deviceTypeSelected === "ControlIdDeviceEntity"}
              />
            )}
            name={"brand"}
            control={control}
          />
        </Grid2>

        {watchConnection === 0 && (
          <>
            <Grid2 size={6}>
              <Controller
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <TextFieldComponent
                    value={value}
                    label={`${t("deviceEditionModal.ip")} *`}
                    errorMessage={error?.message}
                    onChangeText={onChange}
                  />
                )}
                name={"ip"}
                control={control}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <TextFieldComponent
                    value={value}
                    label={`${t("deviceEditionModal.httpPort")} *`}
                    errorMessage={error?.message}
                    onChangeText={onChange}
                  />
                )}
                name={"httpPort"}
                control={control}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <TextFieldComponent
                    value={value}
                    label={`${t("deviceEditionModal.tcpPort")} *`}
                    errorMessage={error?.message}
                    onChangeText={onChange}
                  />
                )}
                name={"tcpPort"}
                control={control}
              />
            </Grid2>
          </>
        )}

        <Grid2 size={6}>
          <Controller
            render={({
              field: { value, onChange },
              fieldState: { error },
            }) => (
              <TextFieldComponent
                value={value}
                label={t("deviceEditionModal.rtspPort")}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={"rtspPort"}
            control={control}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            render={({
              field: { value, onChange },
              fieldState: { error },
            }) => (
              <TextFieldComponent
                value={value}
                label={`${t("deviceEditionModal.username")} *`}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={"username"}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({
              field: { value, onChange },
              fieldState: { error },
            }) => (
              <TextFieldComponent
                value={value}
                label={`${t("deviceEditionModal.password")} *`}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={"password"}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <TextFieldComponent
                value={value}
                label={t("deviceEditionModal.model")}
                onChangeText={onChange}
              />
            )}
            name={"model"}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            name="uc"
            control={control}
            render={({
              field: { value, onChange },
              fieldState: { error },
            }) => (
              <DropdownComponent
                label={t("deviceEditionModal.corporateUnity")}
                value={value}
                errorMessage={error?.message}
                optionLabel="label"
                optionValue="value"
                options={UC_OPTIONS}
                onChange={onChange}
              />
            )}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({
              field: { value, onChange },
              fieldState: { error },
            }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                label={
                  watchBrand === "TOPDATA"
                    ? `${t("deviceEditionModal.serial")} *`
                    : t("deviceEditionModal.serial")
                }
                onChangeText={onChange}
              />
            )}
            name={"serialNumber"}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          {!isDvr ? (
            <Controller
              render={({ field: { value, onChange } }) => (
                <TextFieldComponent
                  value={value}
                  label={t("deviceEditionModal.channel")}
                  type={"number"}
                  onChangeText={(val) => onChange(Number(val))}
                />
              )}
              name={"channel"}
              control={control}
            />
          ) : !deviceToEdit?.id ? (
            <Controller
              render={({ field: { value, onChange } }) => (
                <TextFieldComponent
                  value={value}
                  label={t("deviceEditionModal.numberOfChannels")}
                  type={"number"}
                  onChangeText={(val) => onChange(Number(val))}
                  min={0}
                />
              )}
              name={"numberOfChannels"}
              control={control}
            />
          ) : (
            <span />
          )}
        </Grid2>
        {isDvr && !deviceToEdit?.id && (
          <Grid2 size={6}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <TextFieldComponent
                  value={value}
                  label={t("deviceEditionModal.numberOfAlarmEntries")}
                  type={"number"}
                  onChangeText={(val) => onChange(Number(val))}
                  min={0}
                />
              )}
              name={"numberOfAlarmEntries"}
              control={control}
            />
          </Grid2>
        )}

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <DropdownSelectComponent
                options={availableEventsOptions}
                labelField={"label"}
                valueField={"value"}
                disabled={!deviceTypeSelected}
                selectedValues={value ?? []}
                onSelectedValues={onChange}
                label={t("deviceEditionModal.availableEvents")}
              />
            )}
            name={"availableEvents"}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <RadioSelectionComponent
                options={connectionOptions}
                value={value}
                onChange={(val) => onChange(Number(val))}
                title={t("deviceEditionModal.connectionType")}
              />
            )}
            name={"connection"}
            control={control}
          />
        </Grid2>

        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={
            isLoadingCreateIvsDevice ||
            isLoadingCreateLprDevice ||
            isLoadingCreateRecognitionCamera ||
            isLoadingCreateTemperatureDetectorDevice ||
            isLoadingUpdateIvsDevice ||
            isLoadingUpdateLprDevice ||
            isLoadingUpdateRecognitionCamera ||
            isLoadingUpdateTemperatureDetectorDevice ||
            isLoadingCreateFacialController ||
            isLoadingUpdateFacialController ||
            isLoadingCreateControlIdDevice ||
            isLoadingUpdateControlIdDevice ||
            isLoadingCreateDvr ||
            isLoadingUpdateDvr
          }
          fullWidth
          onClick={() => handleSaveForm()}
        >
          {t("deviceEditionModal.save")}
        </LoadingButton>
      </Grid2>
    </ModalComponent>
  );
};

export default DeviceEditionComponent;
